import { useState } from 'react';
import { restaurantsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { RESTAURANT_STATUSES } from '../lib/restaurantConstants';

const EMPTY_FORM = {
  name: '',
  location: '',
  status: 'backlog',
  cuisine_type: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  monthly_contract_value: 0,
  contract_start: '',
  contract_end: '',
  trial_start: '',
  trial_end: '',
};

export function restaurantToForm(r) {
  if (!r) return { ...EMPTY_FORM };
  return {
    name: r.name || '',
    location: r.location || '',
    status: r.status || 'backlog',
    cuisine_type: r.cuisine_type || '',
    contact_name: r.contact_name || '',
    contact_email: r.contact_email || '',
    contact_phone: r.contact_phone || '',
    monthly_contract_value: r.monthly_contract_value || 0,
    contract_start: r.contract_start || '',
    contract_end: r.contract_end || '',
    trial_start: r.trial_start || '',
    trial_end: r.trial_end || '',
  };
}

export default function RestaurantFormModal({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(() => restaurantToForm(editing));
  const [saving, setSaving] = useState(false);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, monthly_contract_value: parseFloat(form.monthly_contract_value) || 0 };
      if (editing?.id) {
        await restaurantsAPI.update(editing.id, payload);
        toast.success('Restaurant updated');
      } else {
        await restaurantsAPI.create(payload);
        toast.success('Restaurant created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="restaurant-modal">
        <DialogHeader>
          <DialogTitle>{editing?.id ? 'Edit Restaurant' : 'Add New Restaurant'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-[#333]">Name *</label>
            <Input value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="Restaurant name" data-testid="restaurant-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#333]">Status</label>
              <select value={form.status} onChange={e => updateField('status', e.target.value)}
                className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white" data-testid="restaurant-status-select">
                {RESTAURANT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#333]">Location</label>
              <Input value={form.location} onChange={e => updateField('location', e.target.value)} placeholder="City, Country" data-testid="restaurant-location-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#333]">Cuisine</label>
              <Input value={form.cuisine_type} onChange={e => updateField('cuisine_type', e.target.value)} placeholder="Italian, Asian..." data-testid="restaurant-cuisine-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#333]">Contact person</label>
              <Input value={form.contact_name} onChange={e => updateField('contact_name', e.target.value)} placeholder="Full name" data-testid="restaurant-contact-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#333]">Email</label>
              <Input value={form.contact_email} onChange={e => updateField('contact_email', e.target.value)} placeholder="name@restaurant.com" data-testid="restaurant-contact-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#333]">Phone</label>
              <Input value={form.contact_phone} onChange={e => updateField('contact_phone', e.target.value)} placeholder="+971..." data-testid="restaurant-contact-phone-input" />
            </div>
          </div>
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-[#333] mb-3">Contract Period</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#666]">Start</label>
                <Input type="date" value={form.contract_start || ''} onChange={e => updateField('contract_start', e.target.value)} data-testid="restaurant-contract-start" />
              </div>
              <div>
                <label className="text-xs text-[#666]">End</label>
                <Input type="date" value={form.contract_end || ''} onChange={e => updateField('contract_end', e.target.value)} data-testid="restaurant-contract-end" />
              </div>
            </div>
          </div>
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-[#333] mb-3">Trial Period</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#666]">Start</label>
                <Input type="date" value={form.trial_start || ''} onChange={e => updateField('trial_start', e.target.value)} data-testid="restaurant-trial-start" />
              </div>
              <div>
                <label className="text-xs text-[#666]">End</label>
                <Input type="date" value={form.trial_end || ''} onChange={e => updateField('trial_end', e.target.value)} data-testid="restaurant-trial-end" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" onClick={handleSave} disabled={saving} data-testid="submit-restaurant-btn">
            {saving ? 'Saving...' : (editing?.id ? 'Save Changes' : 'Create Restaurant')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
